import { useScrollSpy } from '../lib/useScrollSpy.js';

// The reference sections that come before the sources, in page order. The ids
// are the section ids in pages/DataReference.jsx.
export const GUIDE_SECTIONS = [
  { id: 'how-to-read', label: 'How to read this data' },
  { id: 'field-naming', label: 'Field naming' },
  { id: 'where-data-lives', label: 'Where the data lives' },
  { id: 'file-conventions', label: 'File conventions' },
  { id: 'retention-security', label: 'Retention and security' },
  { id: 'run-files', label: 'Run files' },
  { id: 'access-data', label: 'Access the data' },
  { id: 'field-mapping', label: 'Field mapping' },
];

/**
 * The table of contents as groups of { id, label } entries: the guide
 * sections first, then one group per category with its sources, in the order
 * the reference lists them (GET /data-reference, from data-catalog.mjs).
 */
export function buildTocGroups(reference) {
  return [
    { id: 'guide', title: 'Guide', entries: GUIDE_SECTIONS },
    ...reference.categories.map((cat) => ({
      id: cat.id,
      title: cat.title,
      entries: cat.sources.map((s) => ({ id: s.anchor, label: s.label })),
    })),
  ];
}

// Sections in document order: the guide first, then every source. The order
// the scroll spy uses to pick the current one.
function orderedIds(groups) {
  return groups.flatMap((g) => g.entries.map((e) => e.id));
}

/**
 * Sticky table of contents (wide screens) that collapses to a dropdown at
 * the top of the content (phone width), with the section in view
 * highlighted.
 */
export default function DataReferenceToc({ groups }) {
  const ids = orderedIds(groups);
  const [active, setActive] = useScrollSpy(ids, ids[0] ?? null);

  const jump = (id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView?.({ block: 'start' });
      // Keep the address shareable (#source-<id>) without a router navigation.
      try { window.history.replaceState(null, '', `#${id}`); } catch { /* embedded or sandboxed: the link still scrolled */ }
    }
  };

  return (
    <>
      <div className="sticky top-0 z-20 -mx-4 border-b border-ipc-border bg-white/95 px-4 py-2 backdrop-blur lg:hidden">
        <label htmlFor="data-reference-jump" className="sr-only">Jump to a section</label>
        <select
          id="data-reference-jump"
          value={active ?? ''}
          onChange={(e) => jump(e.target.value)}
          className="input bg-white text-sm text-ipc-ink"
        >
          {groups.map((g) => (
            <optgroup key={g.id} label={g.title}>
              {g.entries.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
            </optgroup>
          ))}
        </select>
      </div>

      <nav
        aria-label="Data reference contents"
        className="hidden lg:block lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:self-start lg:overflow-y-auto pr-2 text-sm"
      >
        {groups.map((g) => (
          <div key={g.id} className="mb-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-ipc-muted">{g.title}</p>
            <ul className="space-y-0.5 border-l border-ipc-border">
              {g.entries.map((e) => {
                const current = e.id === active;
                return (
                  <li key={e.id}>
                    <a
                      href={`#${e.id}`}
                      onClick={(ev) => { ev.preventDefault(); jump(e.id); }}
                      aria-current={current ? 'location' : undefined}
                      className={
                        '-ml-px block border-l-2 py-1 pl-3 pr-1 ' +
                        (current
                          ? 'border-ipc-light font-medium text-ipc-primary'
                          : 'border-transparent text-ipc-ink hover:border-slate-300 hover:text-ipc-primary')
                      }
                    >
                      {e.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </>
  );
}
